import React, {useEffect, useState} from 'react';
import {PaymentElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {toast} from 'react-toastify';
import {useMutation, useQueryClient} from 'react-query';
import {api} from '../helpers';

export function verifyPayment(data) {
  return api.post('/payments/verify/stripe', data).then(res => res.data);
}

export default function StripeCheckoutForm({
  setOpen,
  reference,
  noMessage,
  onSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const {mutateAsync: verifyPaymentMutation} = useMutation(verifyPayment, {
    onSuccess: () => {
      // window.location.reload();
      setMessage('Payment verified successfully!');
      queryClient.invalidateQueries();
    },
    onError: error => {
      console.error('Error making payment:', error);
      toast.error('An error occurred while processing your payment.');
    },
  });

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({paymentIntent}) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          setMessage('Payment succeeded!');
          break;
        case 'processing':
          setMessage('Your payment is processing.');
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          break;
        default:
          setMessage('Something went wrong.');
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const {error, paymentIntent} = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    await verifyPaymentMutation({
      reference,
    });
    setIsLoading(false);

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (
      error &&
      (error?.type === 'card_error' || error?.type === 'validation_error')
    ) {
      setMessage(error.message);
      toast.error(error.message);
      return;
    }
    if (error) {
      setMessage('An unexpected error occurred.');
      toast.error('Payment failed');
      return;
    }
    if (paymentIntent?.status === 'succeeded') {
      setMessage('Payment successful');
      toast.success('Payment successful');
      if (onSuccess) {
        onSuccess();
      } else {
        setOpen(false);
      }
    }
  };

  const handlePaymentElementChange = event => {
    setIsComplete(event.complete);
    if (event.error) {
      setMessage(event.error.message);
    } else {
      setMessage(null);
    }
  };

  const paymentElementOptions = {
    layout: 'tabs',
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="px-6 py-4">
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        onChange={handlePaymentElementChange}
      />
      <button
        className="mt-4 bg-brandBlue w-full flex items-center justify-center h-10 rounded"
        disabled={isLoading || !stripe || !elements || !isComplete}
        id="submit"
      >
        <span id="button-text" className="text-white text-sm font-medium">
          {isLoading ? <div className="spinner" id="spinner"></div> : 'Pay now'}
        </span>
      </button>
      {/* Show any error or success messages */}
      {message && !noMessage && (
        <div id="payment-message" className="mt-3 text-sm">
          {message}
        </div>
      )}
    </form>
  );
}
